
import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Form, Row, Col, Modal, Badge, Spinner, Dropdown } from "react-bootstrap";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { formatDateTime } from "../utils/format";
import "./RequestsList.css"

/**
 * Lists registration requests from:
 *   admin (collection) / regrequest (doc) / requests (subcollection)
 */
export default function RequestsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [search, setSearch] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    const anchorDocRef = doc(db, "admin", "regrequest");
    const requestsColRef = collection(anchorDocRef, "requests");

    // Latest first; throttle to ~200 for admin view, tweak as needed
    const q = query(requestsColRef, orderBy("createdAt", "desc"), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error("onSnapshot error (requests):", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [
        r.communityName,
        r.city,
        r.contactName,
        r.email,
        r.phone,
        r.status,
        r.uid,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, search]);

  async function updateStatus(id, nextStatus) {
    try {
      setStatusUpdating(id);
      const ref = doc(db, "admin", "regrequest", "requests", id);
      await updateDoc(ref, {
        status: nextStatus,
        updatedAt: new Date(),
      });
    } catch (e) {
      console.error("Failed to update status:", e);
      alert("Failed to update status. See console for details.");
    } finally {
      setStatusUpdating(null);
    }
  }

  async function handleDeleteRequest() {
    if (!deleteItem) {
      return;
    }

    if ((deleteItem.status || "").toLowerCase() !== "rejected") {
      setDeleteError("Only rejected requests can be deleted.");
      return;
    }

    if (deleteText.trim() !== "DELETE") {
      setDeleteError('Please enter "DELETE" exactly.');
      return;
    }

    try {
      setDeleting(true);
      setDeleteError("");

      const requestRef = doc(
        db,
        "admin",
        "regrequest",
        "requests",
        deleteItem.id
      );

      await deleteDoc(requestRef);

      setDeleteItem(null);
      setDeleteText("");
    } catch (error) {
      console.error("Failed to delete request:", error);
      setDeleteError("Failed to delete the request. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">Registration Requests</h4>
          <small className="text-muted">Latest enquiries submitted by visitors</small>
        </Col>
        <Col md="auto">
          <Form.Control
            placeholder="Search by name, email, phone, city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" /> <span>Loading…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="alert alert-info">No requests found.</div>
      ) : (
        <div className="requests-table-wrapper">
        <Table hover responsive="md" className="align-middle mb-0">
          <thead>
            <tr>
              <th>Community</th>
              <th>City</th>
              <th>Total Flats</th>
              <th>Contact</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              {/* <th>Created</th> */}
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}>
                <td>{r.communityName || "-"}</td>
                <td>{r.city || "-"}</td>
                <td>{r.totalFlats ?? "-"}</td>
                <td>{r.contactName || "-"}</td>
                <td>{r.email || "-"}</td>
                <td>{r.phone || "-"}</td>
                <td>
                  <Badge bg={badgeForStatus(r.status)}>{r.status || "new"}</Badge>
                </td>
                {/* <td>{formatDateTime(r.createdAt)}</td> */}
                {/* <td className="d-flex flex-wrap gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => setViewItem(r)}>
                    View
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-success"
                    disabled={statusUpdating === r.id}
                    onClick={() => updateStatus(r.id, "contacted")}
                  >
                    {statusUpdating === r.id ? "Updating…" : "Contacted"}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-dark"
                    disabled={statusUpdating === r.id}
                    onClick={() => updateStatus(r.id, "onboarded")}
                  >
                    {statusUpdating === r.id ? "Updating…" : "Onboarded"}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={statusUpdating === r.id}
                    onClick={() => updateStatus(r.id, "rejected")}
                  >
                    {statusUpdating === r.id ? "Updating…" : "Reject"}
                  </Button>
                </td> */}
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => setViewItem(r)}
                    >
                      View
                    </Button>

<td className="text-nowrap">
  <Dropdown
    align="end"
    drop="down"
    popperConfig={{
      strategy: "fixed",
    }}
  >
    <Dropdown.Toggle
      size="sm"
      variant="outline-secondary"
      disabled={statusUpdating === r.id || deleting}
      id={`request-actions-${r.id}`}
    >
      {statusUpdating === r.id ? (
        <>
          <Spinner
            animation="border"
            size="sm"
            className="me-2"
          />
          Updating...
        </>
      ) : (
        "Actions"
      )}
    </Dropdown.Toggle>

    <Dropdown.Menu>
      <Dropdown.Item onClick={() => setViewItem(r)}>
        View Details
      </Dropdown.Item>

      <Dropdown.Divider />

      <Dropdown.Item
        disabled={
          (r.status || "").toLowerCase() === "contacted"
        }
        onClick={() => updateStatus(r.id, "contacted")}
      >
        Mark as Contacted
      </Dropdown.Item>

      <Dropdown.Item
        disabled={
          (r.status || "").toLowerCase() === "onboarded"
        }
        onClick={() => updateStatus(r.id, "onboarded")}
      >
        Mark as Onboarded
      </Dropdown.Item>

      <Dropdown.Item
        disabled={
          (r.status || "").toLowerCase() === "rejected"
        }
        onClick={() => updateStatus(r.id, "rejected")}
      >
        Mark as Rejected
      </Dropdown.Item>

      <Dropdown.Divider />

      <Dropdown.Item
        className={
          (r.status || "").toLowerCase() === "rejected"
            ? "text-danger"
            : "text-muted"
        }
        disabled={
          (r.status || "").toLowerCase() !== "rejected"
        }
        onClick={() => {
          setDeleteItem(r);
          setDeleteText("");
          setDeleteError("");
        }}
      >
        Delete Permanently
      </Dropdown.Item>
    </Dropdown.Menu>
  </Dropdown>
</td>


                   
                  </div>
                </td>
                
              </tr>
            ))}
          </tbody>
        </Table>
        </div>
      )}

      <RequestViewModal item={viewItem} onHide={() => setViewItem(null)} />
      <DeleteRequestModal
        item={deleteItem}
        deleteText={deleteText}
        setDeleteText={setDeleteText}
        deleteError={deleteError}
        deleting={deleting}
        onDelete={handleDeleteRequest}
        onHide={() => {
          if (!deleting) {
            setDeleteItem(null);
            setDeleteText("");
            setDeleteError("");
          }
        }}
      />
    </>
  );
}

function badgeForStatus(s) {
  switch ((s || "").toLowerCase()) {
    case "new":
      return "secondary";
    case "contacted":
      return "info";
    case "onboarded":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "secondary";
  }
}

function RequestViewModal({ item, onHide }) {
  const show = !!item;
  return (
    <div>
<Modal
  show={show}
  onHide={onHide}
  centered
  size="lg"
>
  <Modal.Header
    closeButton
    className="bg-primary text-white"
  >
    <Modal.Title>
      Registration Request Details
    </Modal.Title>
  </Modal.Header>

  <Modal.Body>
    {!item ? (
      <div className="text-center py-5 text-muted">
        No Request Data Found
      </div>
    ) : (
      <>
        {/* Header Section */}
        <div className="request-summary mb-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 p-3 border rounded bg-light">
            <div>
              <h5 className="mb-1 fw-bold">
                {item.communityName || "-"}
              </h5>

              <div className="text-muted small">
                Request ID: {item.uid || item.id}
              </div>
            </div>

            <Badge
              bg={badgeForStatus(item.status)}
              className="px-3 py-2"
            >
              {item.status || "new"}
            </Badge>
          </div>
        </div>

        {/* Community Details */}
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-light fw-bold">
            Community Details
          </div>

          <div className="card-body">
            <Row className="g-3">
              <Col xs={12} md={6}>
                <label className="text-muted small">
                  Community Name
                </label>

                <div className="fw-semibold">
                  {item.communityName || "-"}
                </div>
              </Col>

              <Col xs={6} md={3}>
                <label className="text-muted small">
                  City
                </label>

                <div className="fw-semibold">
                  {item.city || "-"}
                </div>
              </Col>

              <Col xs={6} md={3}>
                <label className="text-muted small">
                  Flats
                </label>

                <div className="fw-semibold">
                  {item.totalFlats ?? "-"}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Contact Details */}
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-light fw-bold">
            Contact Details
          </div>

          <div className="card-body">
            <Row className="g-3">
              <Col xs={12} md={4}>
                <label className="text-muted small">
                  Contact Person
                </label>

                <div className="fw-semibold">
                  {item.contactName || "-"}
                </div>
              </Col>

              <Col xs={12} md={4}>
                <label className="text-muted small">
                  Email Address
                </label>

                <div>
                  {/* {item.email ? ( */}
                    {/* {`mailto:${item.email}`} */}
                      {item.email}
                    {/* // </a> */}
                  {/* ) : ( */}
                    {/* "-" */}
                  {/* )} */}
                </div>
              </Col>

              <Col xs={12} md={4}>
                <label className="text-muted small">
                  Phone Number
                </label>

                <div className="fw-semibold">
                  {item.phone || "-"}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Request Information */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-light fw-bold">
            Request Information
          </div>

          <div className="card-body">
            <Row className="g-3">
              <Col xs={12} md={6}>
                <label className="text-muted small">
                  Created At
                </label>

                <div className="fw-semibold">
                  {formatDateTime(item.createdAt)}
                </div>
              </Col>

              <Col xs={12} md={6}>
                <label className="text-muted small">
                  Current Status
                </label>

                <div>
                  <Badge
                    bg={badgeForStatus(item.status)}
                    className="px-3 py-2"
                  >
                    {item.status || "new"}
                  </Badge>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </>
    )}
  </Modal.Body>

  <Modal.Footer>
    <Button
      variant="outline-secondary"
      onClick={onHide}
    >
      Close
    </Button>
  </Modal.Footer>
</Modal>
    </div>
    // <Modal show={show} onHide={onHide} centered size="lg">
    //   <Modal.Header closeButton>
    //     <Modal.Title>Request Details</Modal.Title>
    //   </Modal.Header>
    //   <Modal.Body>
    //     {!item ? (
    //       <div className="text-muted">No data</div>
    //     ) : (
    //       <div className="row g-3">
    //         <div className="col-12 col-md-6">
    //           <label className="text-muted d-block">Community Name</label>
    //           <div className="fw-semibold">{item.communityName || "-"}</div>
    //         </div>
    //         <div className="col-12 col-md-3">
    //           <label className="text-muted d-block">City</label>
    //           <div className="fw-semibold">{item.city || "-"}</div>
    //         </div>
    //         <div className="col-12 col-md-3">
    //           <label className="text-muted d-block">Total Flats</label>
    //           <div className="fw-semibold">{item.totalFlats ?? "-"}</div>
    //         </div>

    //         <div className="col-12 col-md-4">
    //           <label className="text-muted d-block">Contact Person</label>
    //           <div className="fw-semibold">{item.contactName || "-"}</div>
    //         </div>
    //         <div className="col-12 col-md-4">
    //           <label className="text-muted d-block">Email</label>
    //           <div className="fw-semibold">{item.email || "-"}</div>
    //         </div>
    //         <div className="col-12 col-md-4">
    //           <label className="text-muted d-block">Phone</label>
    //           <div className="fw-semibold">{item.phone || "-"}</div>
    //         </div>

    //         <div className="col-12 col-md-3">
    //           <label className="text-muted d-block">Status</label>
    //           <div>
    //             <Badge bg={badgeForStatus(item.status)}>{item.status || "new"}</Badge>
    //           </div>
    //         </div>
    //         <div className="col-12 col-md-4">
    //           <label className="text-muted d-block">UID / Ref</label>
    //           <div className="fw-semibold">{item.uid || item.id}</div>
    //         </div>
    //         <div className="col-12 col-md-5">
    //           <label className="text-muted d-block">Created At</label>
    //           <div className="fw-semibold">{formatDateTime(item.createdAt)}</div>
    //         </div>
    //       </div>
    //     )}
    //   </Modal.Body>
    //   <Modal.Footer>
    //     <Button variant="secondary" onClick={onHide}>
    //       Close
    //     </Button>
    //   </Modal.Footer>
    // </Modal>
    
  );
}

function DeleteRequestModal({
  item,
  deleteText,
  setDeleteText,
  deleteError,
  deleting,
  onDelete,
  onHide,
}) {
  const canDelete = deleteText.trim() === "DELETE";

  function handleSubmit(event) {
    event.preventDefault();

    if (canDelete && !deleting) {
      onDelete();
    }
  }

  return (
    <Modal
      show={Boolean(item)}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={!deleting}
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger">
            Delete Request Permanently
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-danger">
            <strong>Warning:</strong> This action cannot be undone.
          </div>

          <p className="mb-2">
            You are permanently deleting the registration request for:
          </p>

          <div className="border rounded p-3 mb-3 bg-light">
            <div>
              <strong>Community:</strong>{" "}
              {item?.communityName || "-"}
            </div>

            <div>
              <strong>Contact:</strong>{" "}
              {item?.contactName || "-"}
            </div>

            <div>
              <strong>Email:</strong>{" "}
              {item?.email || "-"}
            </div>

            <div>
              <strong>Status:</strong>{" "}
              <Badge bg={badgeForStatus(item?.status)}>
                {item?.status || "new"}
              </Badge>
            </div>
          </div>

          <Form.Group>
            <Form.Label>
              Enter <strong>DELETE</strong> to confirm:
            </Form.Label>

            <Form.Control
              type="text"
              value={deleteText}
              disabled={deleting}
              autoComplete="off"
              placeholder="Enter DELETE"
              onChange={(event) => {
                setDeleteText(event.target.value);
              }}
            />
          </Form.Group>

          {deleteError && (
            <div className="text-danger mt-2">
              {deleteError}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            disabled={deleting}
            onClick={onHide}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="danger"
            disabled={!canDelete || deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete Permanently"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
